import React, { useEffect } from 'react'

import type {
  Filter,
  TQueryOrderArray,
  TQueryOrderObject,
} from '@cubejs-client/core'
import {
  CubeFilterDimensions,
  DRIFT_CHAT_OPPORTUNITY_OVERVIEW,
  isCubeFilterKeyValueType,
  REPORT_TYPE_TO_FILTER_MAP,
  sanitizedCubeFilters,
} from 'cube'
import { NavigationDatePicker } from 'cube/components/NavigationDatePicker'
import { parseISO, subMonths } from 'date-fns'
import { pathOr } from 'ramda'
import { useLocation } from 'react-router-dom'

import { Box, Flex, Paper, useToast } from '@driftt/dds'
import { parseCubeQueryParams } from '@driftt/dds.reports'
import { NavigationHeader } from '@driftt/tide-core'

import { StyledMainContentContainer } from 'layouts/CubeReportsLayout/constants'
import { OpportunitiesTable } from 'layouts/RevenueReport/Opportunities/OpportunitiesTable'
import { OpportunitiesTableHeader } from 'layouts/RevenueReport/Opportunities/OpportunitiesTableHeader'
import { ReportPage } from 'types/pages'
import { isValidISOString } from 'utils/dates'

import {
  OpportunitiesStoreProvider,
  useOpportunitiesStore,
} from './Opportunities.context'

const DEFAULT_END_DATE = new Date() // Today
const DEFAULT_START_DATE = subMonths(DEFAULT_END_DATE, 1)

const RevenueOpportunitiesContainer = () => {
  const {
    setStore,
    updateStore,
    store: { startDate, endDate },
  } = useOpportunitiesStore()

  const { search } = useLocation()
  const triggerToast = useToast()

  const initializeOpportunitiesStore = () => {
    const { startDate, endDate, filters, order } = parseCubeQueryParams(
      search,
      {
        ignoreQueryPrefix: true,
        arrayLimit: 100,
      }
    )

    let queryParamsOrder

    if (!order) {
      queryParamsOrder = [
        [DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_AMOUNT, 'desc'],
      ]
    } else {
      queryParamsOrder = order
    }

    // Only allow filters that are supported by RevenueOpportunities report
    // Unsupported filters will be dropped w/ a toast message and console warning
    const allowedFilterMembers =
      REPORT_TYPE_TO_FILTER_MAP[ReportPage.RevenueOpportunities]
    const sanitizedFilters = filters
      ? sanitizedCubeFilters(filters as Filter[])
      : []

    const sanitizedAndAllowedFilters = [] as Filter[]
    sanitizedFilters.forEach((genericFilter) => {
      const { keyValueFilters, isKeyValue } =
        isCubeFilterKeyValueType(genericFilter)

      const filter = isKeyValue ? keyValueFilters[0] : genericFilter

      const filterMember = pathOr(
        'unknown',
        ['member'],
        filter
      ) as CubeFilterDimensions

      if (allowedFilterMembers.includes(filterMember)) {
        sanitizedAndAllowedFilters.push(isKeyValue ? genericFilter : filter)
      } else {
        console.warn(`Dropping unsupported filter: ${filterMember}`)
        triggerToast({
          message: 'Removed unsupported filter',
          variant: 'error',
        })
      }
    })

    setStore({
      startDate: isValidISOString(startDate)
        ? parseISO(startDate)
        : DEFAULT_START_DATE,
      endDate: isValidISOString(endDate) ? parseISO(endDate) : DEFAULT_END_DATE,
      filters: sanitizedAndAllowedFilters,
      order: queryParamsOrder as TQueryOrderArray | TQueryOrderObject,
    })
  }

  /**
   * Initialize OpportunitiesStore on mount. Use valid query param values or defaults.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(initializeOpportunitiesStore, [])

  return (
    <Box className="reports-layout">
      <NavigationHeader title="Opportunity data">
        <NavigationDatePicker
          startDate={startDate}
          endDate={endDate}
          updateDates={updateStore}
        />
      </NavigationHeader>
      <StyledMainContentContainer
        fullWidth
        fullHeight
        direction="column"
        gap={2}
      >
        <Paper
          fullWidth
          elevation={2}
          as={Flex}
          gap={0}
          direction="column"
          alignItems={'stretch'}
        >
          <OpportunitiesTableHeader />
          <OpportunitiesTable />
        </Paper>
      </StyledMainContentContainer>
    </Box>
  )
}

export const RevenueOpportunitiesReport = () => {
  return (
    <OpportunitiesStoreProvider>
      <RevenueOpportunitiesContainer />
    </OpportunitiesStoreProvider>
  )
}
